import { CommonModule } from '@angular/common';
import {
  Component,
  forwardRef,
  input,
  output,
  signal,
  effect,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';

import {
  VOneChipsTagsComponent,
  VOneDropdownButtonComponent,
} from '@shared-components';
import { IconType } from '@shared-enums';
import { ButtonVariant, DropdownOption } from '@shared-models';

@Component({
  selector: 'v-one-filter',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    forwardRef(() => VOneDropdownButtonComponent),
    forwardRef(() => VOneChipsTagsComponent),
  ],
  templateUrl: './v-one-filter.component.html',
  styleUrls: ['./v-one-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VOneFilterComponent {
  private translate = inject(TranslateService);

  readonly ICON_TYPE = IconType;

  options = input.required<DropdownOption[]>();
  initialSelection = input<string[]>([]);
  disabled = input<boolean>(false);
  variant = input<ButtonVariant>('text');
  placeholder = input<string>(this.translate.instant('Filtrar'));
  closeOnSelect = input<boolean>(false);
  showChips = input<boolean>(true);

  filterChange = output<string[]>();
  chipRemoved = output<string>();

  readonly filterControl = new FormControl<string[]>([], {
    nonNullable: true,
  });

  readonly currentFilterValues = signal<string[]>([]);

  readonly hasActiveFilters = signal<boolean>(false);

  readonly activeFilterCount = signal<number>(0);

  readonly activeFilterOptions = signal<DropdownOption[]>([]);

  constructor() {
    this.setupInitialSelection();
    this.setupDisabledState();
    this.setupValueChanges();
  }

  private setupInitialSelection(): void {
    effect(() => {
      const initial = this.initialSelection();
      const opts = this.options();

      if (!opts || opts.length === 0) return;

      const valuesToSet =
        initial.length > 0 ? initial : opts.map((opt) => opt.value);

      this.filterControl.setValue(valuesToSet, { emitEvent: false });
      this.updateSignals(valuesToSet);
    });
  }

  private setupDisabledState(): void {
    effect(() => {
      const isDisabled = this.disabled();
      if (isDisabled) {
        this.filterControl.disable({ emitEvent: false });
      } else {
        this.filterControl.enable({ emitEvent: false });
      }
    });
  }

  private setupValueChanges(): void {
    this.filterControl.valueChanges.subscribe((values) => {
      this.updateSignals(values || []);
      this.filterChange.emit(values || []);
    });
  }

  private updateSignals(values: string[]): void {
    this.currentFilterValues.set(values);
    this.hasActiveFilters.set(values.length > 0);
    this.activeFilterCount.set(values.length);

    const opts = this.options();
    const activeOptions = values
      .map((value) => opts.find((opt) => opt.value === value))
      .filter((opt): opt is DropdownOption => opt !== undefined);

    this.activeFilterOptions.set(activeOptions);
  }

  removeFilter(value: string): void {
    const currentValues = this.filterControl.value || [];
    const newValues = currentValues.filter((v) => v !== value);
    this.filterControl.setValue(newValues);
    this.chipRemoved.emit(value);
  }
}
